/**
 * Importação rápida de CSV simples sem IA (primeira linha = cabeçalho opcional).
 */

export type CsvBulkRow = {
  name: string;
  code: string | null;
  price: number | null;
  stock: number | null;
  categoryName: string | null;
  supplierName: string | null;
};

function splitLine(line: string, delimiter: string): string[] {
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && ch === delimiter) {
      out.push(cur.trim());
      cur = "";
      continue;
    }
    cur += ch;
  }
  out.push(cur.trim());
  return out;
}

function detectDelimiter(firstLine: string): string {
  const commas = (firstLine.match(/,/g) || []).length;
  const semis = (firstLine.match(/;/g) || []).length;
  return semis > commas ? ";" : ",";
}

function parseNumber(raw: string): number | null {
  const t = raw.replace(/[^\d,.-]/g, "").replace(",", ".");
  if (!t) return null;
  const n = parseFloat(t);
  return Number.isNaN(n) ? null : n;
}

function parseIntSafe(raw: string): number | null {
  const t = raw.replace(/\D/g, "");
  if (!t) return null;
  const n = parseInt(t, 10);
  return Number.isNaN(n) ? null : n;
}

type ColKey = "name" | "code" | "price" | "stock" | "categoryName" | "supplierName";

const HEADER_MAP: Record<string, ColKey> = {
  nome: "name",
  name: "name",
  produto: "name",
  produtos: "name",
  descricao: "name",
  codigo: "code",
  code: "code",
  sku: "code",
  ean: "code",
  barras: "code",
  codigodebarras: "code",
  preco: "price",
  price: "price",
  valor: "price",
  estoque: "stock",
  stock: "stock",
  qtd: "stock",
  quantidade: "stock",
  categoria: "categoryName",
  category: "categoryName",
  fornecedor: "supplierName",
  supplier: "supplierName",
};

function mapHeader(cells: string[]): Partial<Record<ColKey, number>> | null {
  const idx: Partial<Record<ColKey, number>> = {};
  let anyMapped = false;
  cells.forEach((cell, i) => {
    const norm = cell
    .replace(/\s/g, "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
  const key = HEADER_MAP[norm];
    if (key) {
      idx[key] = i;
      anyMapped = true;
    }
  });
  return anyMapped ? idx : null;
}

export function parseProductsFromCsv(text: string): CsvBulkRow[] {
  const lines = text
    .trim()
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];

  const delim = detectDelimiter(lines[0]);
  const firstCells = splitLine(lines[0], delim).map((c) =>
    c.replace(/^"|"$/g, "").trim()
  );

  const headerIdx = mapHeader(firstCells);
  const dataLines = headerIdx ? lines.slice(1) : lines;

  const rows: CsvBulkRow[] = [];
  for (const line of dataLines) {
    const cells = splitLine(line, delim).map((c) => c.replace(/^"|"$/g, "").trim());
    if (cells.every((c) => !c)) continue;

    if (headerIdx) {
      const ni = headerIdx.name ?? 0;
      const name = cells[ni] ?? "";
      if (!name) continue;
      const codeRaw =
        headerIdx.code !== undefined ? (cells[headerIdx.code] ?? "").trim() : "";
      const price =
        headerIdx.price !== undefined ? parseNumber(cells[headerIdx.price] ?? "") : null;
      const stock =
        headerIdx.stock !== undefined ?
          parseIntSafe(cells[headerIdx.stock] ?? "") :
          null;
      const categoryName =
        headerIdx.categoryName !== undefined ?
          cells[headerIdx.categoryName] || null :
          null;
      const supplierName =
        headerIdx.supplierName !== undefined ?
          cells[headerIdx.supplierName] || null :
          null;
      rows.push({
        name,
        code: codeRaw || null,
        price,
        stock,
        categoryName,
        supplierName,
      });
    } else {
      const name = cells[0] ?? "";
      if (!name) continue;
      if (cells.length >= 6) {
        rows.push({
          name,
          code: (cells[1] ?? "").trim() || null,
          price: cells[2] ? parseNumber(cells[2]) : null,
          stock: cells[3] ? parseIntSafe(cells[3]) : null,
          categoryName: cells[4] || null,
          supplierName: cells[5] || null,
        });
      } else {
        rows.push({
          name,
          code: null,
          price: cells[1] ? parseNumber(cells[1]) : null,
          stock: cells[2] ? parseIntSafe(cells[2]) : null,
          categoryName: cells[3] || null,
          supplierName: cells[4] || null,
        });
      }
    }
  }

  return rows;
}
